import { getEntityTagFromSnake, getTickersFromSnakes } from '@toggle/helpers';
import { DetailedCatalystInsight } from '@toggle/toggle';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { ErrorBoundaryWrapper } from '~/components/error-boundary-wrapper/ErrorBoundaryWrapper';
import { InsightTypePill } from '~/components/insight-type-pill/InsightTypePill';
import { InsightsLivePrice } from '~/components/insights-live-price/InsightsLivePrice';
import { useMarketReactionData } from '~/hooks/use-market-reaction-data/useMarketReactionData';
import { MarketReactionChart } from '~/modules/market-reaction-charts/MarketReactionChart';
import { useEntity } from '~/react-query-hooks/entities/useEntities';
import { useTheme } from '~/react-query-hooks/themes/useThemeOverview';
import { ThemeLogoIcon } from '~/views/theme-overview/components/theme-logo-icon/ThemeLogoIcon';
import { EntityHeaderWatchlist } from '~/widgets/earnings-insight-drawer/earnings-insight-widget/components/entity-header-watchlist/EntityHeaderWatchlist';

import { MarketCatalystReaction } from '../market-catalyst-reaction/MarketCatalystReaction';
import * as S from './CatalystDrawerHeader.styles';

export interface CatalystDrawerHeaderProps {
  identifier: string;
  catalystInsight: DetailedCatalystInsight;
  type: 'company' | 'theme';
}

export const CatalystDrawerHeader = ({
  identifier,
  catalystInsight,
  type,
}: CatalystDrawerHeaderProps) => {
  const { t } = useTranslation('insights');
  const companyEntityTag = type === 'company' ? identifier : undefined;
  const themeId = type === 'theme' ? identifier : undefined;
  const entityTag = companyEntityTag
    ? companyEntityTag
    : catalystInsight.snakes.length === 1
    ? getEntityTagFromSnake(catalystInsight.snakes[0])
    : undefined;
  const { data: entityData } = useEntity(entityTag ?? '');

  const { data: themeData } = useTheme({
    themeId,
  });

  const entity = entityData?.data;
  const isCompanyCatalyst = type === 'company';

  const { priceChangeData, fromDateEST, toDateEST } = useMarketReactionData({
    ticker: entity?.subscribable_ticker ?? '',
    reportingDate: catalystInsight.created_at,
    enabled: isCompanyCatalyst,
  });

  /* i18next-extract-mark-context-next-line ["financial_operational_updates", "corporate_actions_strategic_announcements", "management_leadership_changes", "legal_regulatory_events"] */
  const translatedSubCategory = t(
    `catalyst.subcategories.${catalystInsight.sub_category}`
  );

  return (
    <S.Root>
      <InsightTypePill createdAt={catalystInsight.created_at} type={type} />
      <div>
        <S.SubCategoryActionTypeWrapper>
          {themeId ? (
            <S.TitleWrapper>
              <span>{`${catalystInsight.action_type} · `}</span>
              {themeData && (
                <S.ThemeTitleWrapper>
                  <ThemeLogoIcon
                    logo_url={themeData.logo_url}
                    name={themeData.name}
                    size={20}
                  />
                  <S.ThemeTitle>{themeData.name}</S.ThemeTitle>
                </S.ThemeTitleWrapper>
              )}
            </S.TitleWrapper>
          ) : (
            `${translatedSubCategory} · ${catalystInsight.action_type}`
          )}
        </S.SubCategoryActionTypeWrapper>
        <S.Title>{catalystInsight.title}</S.Title>
      </div>
      {entity && (
        <S.FlexWrapper>
          <EntityHeaderWatchlist entity={entity} />
          <InsightsLivePrice entity={entity} showPricePercentage />
        </S.FlexWrapper>
      )}
      <ErrorBoundaryWrapper>
        {isCompanyCatalyst ? (
          <MarketReactionChart
            fromDateEST={fromDateEST}
            toDateEST={toDateEST}
            lines={priceChangeData ? [priceChangeData] : []}
          />
        ) : (
          <MarketCatalystReaction
            createdAt={catalystInsight.created_at}
            tickers={getTickersFromSnakes(catalystInsight.snakes)}
            tags={catalystInsight.snakes.map(snake =>
              getEntityTagFromSnake(snake)
            )}
          />
        )}
      </ErrorBoundaryWrapper>
    </S.Root>
  );
};
